import { createSlice, PayloadAction } from '@reduxjs/toolkit';

interface ConfirmPopup {
  action: (() => void) | null;
  open: boolean;
  type: string | null;
  msg: string | null;
}

interface OpenConfirm {
  okTitle: string | null;
  title: string | null;
  action: (() => void) | null;
  open: boolean;
  type: string | null;
  msg: string | null;
}

export interface MessagePayload {
  title: string;
  content?: string;
}

export interface AppState {
  loading: boolean;
  error: MessagePayload | null;
  info: MessagePayload | null;
  success: MessagePayload | null;
  onOk: (() => void) | null;
  lang: string;
  confirmPopup: ConfirmPopup;
  openConfirm: OpenConfirm;
}

const initialState: AppState = {
  loading: false,
  error: null,
  info: null,
  success: null,
  onOk: null,
  lang: localStorage.getItem('i18nextLng')?.toLowerCase() || 'ko',
  confirmPopup: {
    action: null,
    open: false,
    type: null,
    msg: null
  },
  openConfirm: {
    okTitle: null,
    title: null,
    action: null,
    open: false,
    type: null,
    msg: null
  }
};

export const appSlice = createSlice({
  name: 'app',
  initialState,
  reducers: {
    setLoading: (state, { payload }: PayloadAction<boolean>) => {
      state.loading = payload;
    },
    setError: (state, { payload }: PayloadAction<MessagePayload | null>) => {
      state.error = payload;
    },
    setInfo: (state, { payload }: PayloadAction<MessagePayload | null>) => {
      state.info = payload;
    },
    setSuccess: (state, { payload }: PayloadAction<MessagePayload | null>) => {
      state.success = payload;
    },
    setOnOk: (state, { payload }: PayloadAction<(() => void) | null>) => {
      state.onOk = payload;
    },
    setLang: (state, { payload }: PayloadAction<string>) => {
      state.lang = payload;
    },
    onOpenConfirm: (state, { payload }: PayloadAction<Partial<ConfirmPopup>>) => {
      const { open, type, action, msg } = payload;
      state.confirmPopup.open = open || false;
      state.confirmPopup.type = type || null;
      state.confirmPopup.action = action || null;
      state.confirmPopup.msg = msg || null;
    },
    setOpenConfirm: (state, { payload }: PayloadAction<Partial<OpenConfirm>>) => {
      const { open, type, action, msg, okTitle, title } = payload;
      state.openConfirm.open = open || false;
      state.openConfirm.type = type || null;
      state.openConfirm.action = action || null;
      state.openConfirm.msg = msg || null;
      state.openConfirm.okTitle = okTitle || null;
      state.openConfirm.title = title || null;
    }
  }
});

export const {
  setLoading,
  setError,
  setInfo,
  setSuccess,
  setOnOk,
  setLang,
  onOpenConfirm,
  setOpenConfirm
} = appSlice.actions;

export default appSlice.reducer;
